import { z } from 'zod';
import { IsoDateSchema } from '@alafec/shared';
import { ApiError } from './errors';
import type { CloseBlockerDto } from './schemas';

function isHttpPlaceholder(message: string): boolean {
  return /^HTTP \d{3}$/.test(message);
}

const BLOCKER_LABELS: Record<CloseBlockerDto['code'], readonly [string, string]> = {
  UNKNOWN_EVENT: ['transaction non classée', 'transactions non classées'],
  UNCLASSIFIED_RAW: ['transaction sans classification', 'transactions sans classification'],
  EXCLUDED_ASSET: ['actif non comptabilisable', 'actifs non comptabilisables'],
  MISSING_PRICE: ['prix manquant', 'prix manquants'],
  MISSING_CLOSING_PRICE: ['prix de clôture manquant', 'prix de clôture manquants'],
  FUTURE_PERIOD_END: ['fin de période future', 'fins de période futures'],
  CUMP_UNRECONSTRUCTABLE: ['historique CUMP incomplet', 'historiques CUMP incomplets'],
  UNMATCHED_BRIDGE: ['jambe de bridge non appariée', 'jambes de bridge non appariées'],
  LIAISON_RESIDUAL: ['solde de liaison non apuré', 'soldes de liaison non apurés'],
  SUSPENSE_RESIDUAL: ['compte d’attente non apuré', 'comptes d’attente non apurés'],
  ASSET_UNREVIEWED: [
    'traitement comptable d’actif à revoir',
    'traitements comptables d’actifs à revoir',
  ],
  ASSET_REQUIRES_EC: [
    'actif à valider avec l’expert-comptable',
    'actifs à valider avec l’expert-comptable',
  ],
  UNSUPPORTED_STAKING: [
    'opération de staking à qualifier avec l’expert-comptable',
    'opérations de staking à qualifier avec l’expert-comptable',
  ],
  SPAM_UNREVIEWED: ['spam à confirmer', 'spams à confirmer'],
  RPC_MISSING: ['configuration RPC manquante', 'configurations RPC manquantes'],
  RECONCILIATION_MISSING: ['réconciliation manquante', 'réconciliations manquantes'],
  RECONCILIATION_FAILED: ['réconciliation en échec', 'réconciliations en échec'],
  RECONCILIATION_STALE: ['réconciliation périmée', 'réconciliations périmées'],
  RECONCILIATION_DIVERGENT: ['solde on-chain divergent', 'soldes on-chain divergents'],
};

const FALLBACK_BLOCKER_LABEL: readonly [string, string] = [
  'blocage comptable',
  'blocages comptables',
];
const UNKNOWN_CODES_KEY = '__OTHER__';

const ClosingPriceMissingBodySchema = z
  .object({
    error: z.literal('ClosingPriceMissingError'),
    missing: z
      .array(
        z
          .object({
            assetId: z.number().int().positive(),
            date: IsoDateSchema,
          })
          .strict(),
      )
      .min(1),
  })
  .passthrough();

export interface MissingClosingPrice {
  readonly assetId: number;
  readonly date: string;
}

export function closingPricesMissing(error: unknown): readonly MissingClosingPrice[] | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null;
  const parsed = ClosingPriceMissingBodySchema.safeParse(error.body);
  return parsed.success ? parsed.data.missing : null;
}

function isKnownBlockerCode(code: string): code is CloseBlockerDto['code'] {
  return Object.hasOwn(BLOCKER_LABELS, code);
}

function closeBlockersOf(body: unknown): readonly unknown[] | null {
  if (body === null || typeof body !== 'object' || !('blockers' in body)) return null;
  const blockers = (body as { blockers?: unknown }).blockers;
  return Array.isArray(blockers) && blockers.length > 0 ? blockers : null;
}

// Permet aux pages de réagir à un refus de clôture (ex. naviguer vers les contrôles).
export function isCloseBlockedError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422 && closeBlockersOf(error.body) !== null;
}

function closeBlockerMessage(body: unknown): string | null {
  const blockers = closeBlockersOf(body);
  if (blockers === null) return null;
  const counts = new Map<string, number>();
  for (const blocker of blockers) {
    if (blocker !== null && typeof blocker === 'object' && 'code' in blocker) {
      const code = String((blocker as { code: unknown }).code);
      // Codes inconnus fusionnés en un seul décompte — sinon chaque code produit
      // un segment générique dupliqué (« 4 blocages comptables · 10 blocages comptables »).
      const key = isKnownBlockerCode(code) ? code : UNKNOWN_CODES_KEY;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const details = Array.from(counts.entries()).map(([key, count]) => {
    const label = isKnownBlockerCode(key) ? BLOCKER_LABELS[key] : FALLBACK_BLOCKER_LABEL;
    return `${count} ${count === 1 ? label[0] : label[1]}`;
  });
  return details.length > 0
    ? `Clôture impossible : ${details.join(' · ')} — détail dans « Préparation de la clôture ».`
    : null;
}

function closingPriceMissingMessage(error: ApiError): string | null {
  const missing = closingPricesMissing(error);
  if (missing === null) return null;
  return missing.length === 1
    ? 'Clôture impossible : un prix de clôture est manquant. Saisissez-le depuis « Préparation de la clôture ».'
    : `Clôture impossible : ${missing.length} prix de clôture sont manquants. Saisissez-les depuis « Préparation de la clôture ».`;
}

function futurePeriodCloseMessage(body: unknown): string | null {
  if (body === null || typeof body !== 'object') return null;
  const { error, periodEnd } = body as { error?: unknown; periodEnd?: unknown };
  if (error !== 'FuturePeriodCloseError' || typeof periodEnd !== 'string') return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(periodEnd)) return null;
  const frenchDate = `${periodEnd.slice(8, 10)}/${periodEnd.slice(5, 7)}/${periodEnd.slice(0, 4)}`;
  return `Clôture impossible : l’exercice se termine le ${frenchDate} — la clôture ne pourra être lancée qu’après cette date.`;
}

// Message français actionnable pour le toast global — jamais de code HTTP brut.
export function frenchErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const serverMessage = isHttpPlaceholder(error.message) ? null : error.message;
    if (error.status === 401 || error.status === 403) {
      return 'Accès refusé — votre session a peut-être expiré. Rechargez la page.';
    }
    if (error.status === 404) return 'Ressource introuvable.';
    if (error.status === 409) {
      return (
        futurePeriodCloseMessage(error.body) ??
        (serverMessage !== null
          ? `Conflit : ${serverMessage}`
          : 'Conflit avec l’état actuel — rechargez la page et réessayez.')
      );
    }
    if (error.status === 422) {
      return (
        closingPriceMissingMessage(error) ??
        closeBlockerMessage(error.body) ??
        serverMessage ??
        'Clôture impossible.'
      );
    }
    if (error.status === 429) return 'Trop de requêtes — réessayez dans un instant.';
    if (error.status === 502 && serverMessage !== null) return serverMessage;
    if (error.status >= 500) {
      return serverMessage !== null
        ? `Erreur serveur : ${serverMessage}`
        : 'Erreur serveur — réessayez ; si le problème persiste, consultez les logs du worker.';
    }
    return serverMessage ?? 'Requête invalide.';
  }
  if (error instanceof TypeError) {
    return 'Connexion impossible — vérifiez le réseau ou que le worker est démarré.';
  }
  return error instanceof Error && error.message.length > 0
    ? error.message
    : 'Une erreur est survenue';
}
